import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Alert, CircularProgress } from '@mui/material';
import { Assessment as AnalyticsIcon, Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import AwardPoints from './AwardPoints';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface UserPoints {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  position?: string;
  // Removed role - use permission templates instead
  pointsBalance: number;
  monthlyEarned: number;
  totalEarned: number;
  rank: number;
}

interface PointsSummary {
  totalPoints: number;
  totalMonthlyEarned: number;
  activeUsers: number;
  totalUsers: number;
}

const PointsDistribution: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserPoints[]>([]);
  const [summary, setSummary] = useState<PointsSummary>({
    totalPoints: 0,
    totalMonthlyEarned: 0,
    activeUsers: 0,
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [awardPointsOpen, setAwardPointsOpen] = useState(false);

  useEffect(() => {
    fetchPointsDistribution();
  }, []);

  const fetchPointsDistribution = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(API_ENDPOINTS.DISTRIBUTION), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('pointsDistribution.failedToFetchDistribution'));
      }

      const data = await response.json();
      setUsers(data.users);
      setSummary(data.summary);
    } catch (err) {
      console.error('Error fetching points distribution:', err);
      setError(t('pointsDistribution.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleAwardPointsSuccess = () => {
    // Refresh the points distribution data
    fetchPointsDistribution();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          {t('pointsDistribution.title')}
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('pointsDistribution.title')}
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('pointsDistribution.totalPointsInSystem')}
            </Typography>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
              {summary.totalPoints.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('pointsDistribution.monthlyPointsDistributed')}
            </Typography>
            <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
              {summary.totalMonthlyEarned.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('pointsDistribution.activeUsers')}
            </Typography>
            <Typography variant="h3" color="info.main" sx={{ fontWeight: 'bold' }}>
              {summary.activeUsers}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Distribution Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              {t('pointsDistribution.userPointsDistribution')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setAwardPointsOpen(true)}
            >
              {t('pointsDistribution.awardPointsButton')}
            </Button>
          </Box>

          {users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t('pointsDistribution.noUsersFound')}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pointsDistribution.rank')}</TableCell>
                    <TableCell>{t('pointsDistribution.user')}</TableCell>
                    <TableCell>{t('pointsDistribution.department')}</TableCell>
                    {/* Removed role column - use permission templates instead */}
                    <TableCell align="right">{t('pointsDistribution.currentPoints')}</TableCell>
                    <TableCell align="right">{t('pointsDistribution.monthlyEarned')}</TableCell>
                    <TableCell align="right">{t('pointsDistribution.totalEarned')}</TableCell>
                    <TableCell align="center">{t('pointsDistribution.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Chip
                          label={`#${user.rank}`}
                          color={user.rank <= 3 ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.department}
                        </Typography>
                      </TableCell>
                      {/* Removed role cell - use permission templates instead */}
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {user.pointsBalance.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          +{user.monthlyEarned.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {user.totalEarned.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setAwardPointsOpen(true)}
                        >
                          {t('pointsDistribution.award')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Award Points Dialog */}
      <AwardPoints
        open={awardPointsOpen}
        onClose={() => setAwardPointsOpen(false)}
        onSuccess={handleAwardPointsSuccess}
      />
    </Box>
  );
};

export default PointsDistribution; 